import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { 
  Receipt,
  DollarSign,
  Calendar,
  CreditCard,
  Building,
  ShoppingCart,
  Wifi,
  FileText,
  Plane,
  Megaphone,
  MoreHorizontal,
} from "lucide-react";

interface Expense {
  id: string;
  description: string;
  amount: number;
  date: string;
  category: string;
  paymentMethod: string;
  status: 'Pending' | 'Paid';
}

interface ViewExpenseDialogProps {
  expense: Expense | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'Rent':
      return <Building className="h-5 w-5 text-blue-600" />;
    case 'Utilities':
      return <Wifi className="h-5 w-5 text-blue-600" />;
    case 'Supplies':
      return <ShoppingCart className="h-5 w-5 text-blue-600" />;
    case 'Software':
      return <FileText className="h-5 w-5 text-blue-600" />;
    case 'Travel':
      return <Plane className="h-5 w-5 text-blue-600" />;
    case 'Marketing':
      return <Megaphone className="h-5 w-5 text-blue-600" />;
    default:
      return <Receipt className="h-5 w-5 text-blue-600" />;
  }
};

export function ViewExpenseDialog({ expense, open, onOpenChange }: ViewExpenseDialogProps) {
  if (!expense) return null;

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] h-[90vh] flex flex-col p-0 rounded-xl overflow-hidden">
        <DialogHeader className="sr-only">
          <DialogTitle>Expense Details - {expense.description}</DialogTitle>
          <DialogDescription>Detailed view of the selected expense</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Expense Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100 shadow-sm">
              <div className="flex items-center">
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    {expense.description || 'Expense'}
                  </h1>
                  <div className="flex items-center gap-3">
                    <Badge
                      variant={expense.status === 'Paid' ? 'default' : 'secondary'}
                      className="text-sm font-semibold px-3 py-1"
                    >
                      {expense.status}
                    </Badge>
                    <span className="text-gray-600 text-base font-medium">
                      {formatCurrency(expense.amount)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="bg-blue-50 rounded-xl p-6 shadow-sm border border-blue-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Receipt className="h-5 w-5 text-blue-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Basic Information</h2>
                </div>
                
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                      <FileText className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">Description</p>
                      <p className="font-semibold text-gray-900">{expense.description || "Not provided"}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                      {getCategoryIcon(expense.category)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">Category</p>
                      <p className="font-semibold text-gray-900">{expense.category || "Not provided"}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                      <DollarSign className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">Amount</p>
                      <p className="font-semibold text-gray-900 text-lg">{formatCurrency(expense.amount)}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                      <Calendar className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">Date</p>
                      <p className="font-semibold text-gray-900">{formatDate(expense.date)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Information */}
              <div className="bg-green-50 rounded-xl p-6 shadow-sm border border-green-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                    <CreditCard className="h-5 w-5 text-green-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Payment Information</h2>
                </div>
                
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                      <CreditCard className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">Payment Method</p>
                      <p className="font-semibold text-gray-900">{expense.paymentMethod || "Not specified"}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                      <Receipt className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">Status</p>
                      <Badge
                        variant={expense.status === 'Paid' ? 'default' : 'secondary'}
                        className={`font-semibold ${
                          expense.status === 'Paid' 
                            ? 'bg-green-100 text-green-700 border-green-200' 
                            : 'bg-yellow-100 text-yellow-700 border-yellow-200'
                        }`}
                      >
                        {expense.status}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Notes Section (if needed in future) */}
            <div className="bg-purple-50 rounded-xl p-6 shadow-sm border border-purple-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <FileText className="h-5 w-5 text-purple-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Additional Information</h2>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                  <MoreHorizontal className="h-4 w-4 text-purple-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-500 mb-2">Expense Details</p>
                  <p className="font-semibold text-gray-900 leading-relaxed">
                    This expense was recorded on {formatDate(expense.date)}. 
                    {expense.status === 'Paid' 
                      ? ' The payment has been completed successfully.' 
                      : ' This expense is currently pending payment.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

